import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { isDefined } from "@utils/helpers";

import { fetchRoutes } from "../helpers/woosmap.helpers";
import { DOT_MARKER_URL, ZOOM_INITIAL } from "../storeDirections.config";
import { useMap } from "./useMap.hook";
import { useWoosmapServices } from "@utils/hooks/useWoosmapServices.hook";

type UseDirectionsProps = {
    origin?: woosmap.map.LatLngLiteral;
    destination: woosmap.map.LatLngLiteral;
    mapElementRef: RefObject<HTMLDivElement>;
};

export const useDirections = ({
    origin,
    destination,
    mapElementRef,
}: UseDirectionsProps) => {
    const map = useMap(mapElementRef, destination);

    const { DirectionsService } = useWoosmapServices();

    const directionsRendererRef = useRef<woosmap.map.DirectionsRenderer>(
        new woosmap.map.DirectionsRenderer({
            preserveViewport: true
        }),
    );

    const originMarkerRef = useRef<woosmap.map.Marker>(
        new woosmap.map.Marker(),
    );

    const [route, setRoute] = useState<woosmap.map.DirectionRoute | undefined>(
        undefined,
    );

    const renderDirections = useCallback(
        (origin: woosmap.map.LatLngLiteral) => fetchRoutes(
            {
                origin: origin,
                destination: destination,
            },
            DirectionsService,
        )
            .then(directions => {
                directionsRendererRef.current.setMap(map ?? null);
                directionsRendererRef.current.setDirections(directions);

                return directions.routes.at(0);
            })
            .then((route) => {
                setRoute(route);

                if (isDefined(route)) {
                    const bounds = new woosmap.map.LatLngBounds(route.bounds.northeast, route.bounds.southwest);
                    const padding: woosmap.map.Padding = { top: 210, bottom: 32, left: 32, right: 32 }

                    map?.fitBounds(bounds, padding, true);
                }
            }),
        [
            destination.lat,
            destination.lng,
            directionsRendererRef,
            map,
            DirectionsService
        ]
    )

    const clearDirections = useCallback(
        () => directionsRendererRef.current.setDirections({
            routes: [],
            status: "OK",
        }),
        [directionsRendererRef],
    );

    const renderOriginMarker = useCallback(
        (origin: woosmap.map.LatLngLiteral) => {
            originMarkerRef.current.setIcon({
                url: DOT_MARKER_URL,
                scaledSize: {
                    height: 15,
                    width: 15
                }
            });
            originMarkerRef.current.setPosition(origin);
            originMarkerRef.current.setMap(map);
        },
        [originMarkerRef, map]
    );

    const clearOriginMarker = useCallback(
        () => originMarkerRef.current.setMap(null),
        [originMarkerRef]
    );

    const resetMapCenter = useCallback(
        () => {
            map?.flyTo({
                center: destination,
                zoom: ZOOM_INITIAL
            })
        },
        [map, destination]
    );

    useEffect(() => {
        if (isDefined(origin)) {
            renderDirections(origin);
            renderOriginMarker(origin)
        } else {
            clearDirections();
            clearOriginMarker();
            resetMapCenter();
        }
    }, [
        renderDirections,
        clearDirections,
        renderOriginMarker,
        clearOriginMarker,
        resetMapCenter,
        origin?.lat,
        origin?.lat,
    ]);

    return route;
};
